import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from '@/hooks/use-toast';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface MigrationResult {
  total?: number;
  migrated?: number;
  skipped?: number;
  failed?: number;
  details?: any[];
  success?: boolean;
  service?: number;
  error?: string;
  message?: string;
}

export const ServiceImageMigrationTool = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [forceMode, setForceMode] = useState(false);
  const { toast } = useToast();

  const handleMigrate = async () => {
    try {
      setLoading(true);
      setResult(null);
      
      // Get the current session to extract the authentication token
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        console.error('Authentication error:', sessionError || 'No active session');
        throw new Error('Authentication error: ' + (sessionError?.message || 'No active session'));
      }
      
      // Extract the access token from the session
      const token = sessionData.session.access_token;
      console.log('Got authenticated token for migration');
      
      // Call the edge function with authentication headers
      const { data, error } = await supabase.functions.invoke('migrate-service-images', {
        body: { force: forceMode },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (error) {
        console.error('Migration error:', error);
        
        // More descriptive error handling for specific error cases
        if (error.message && error.message.includes('401')) {
          throw new Error('Authentication failed. Please ensure you have admin privileges.');
        } else if (error.message && error.message.includes('403')) {
          throw new Error('Access denied. You need admin permissions to perform this operation.');
        } else {
          throw new Error(`Migration error: ${error.message || 'Unknown error'}`);
        }
      }
      
      console.log('Migration result:', data);
      setResult(data);
      
      toast({
        title: 'Migration Complete',
        description: `Successfully migrated ${data.migrated} service images${data.skipped ? `, ${data.skipped} already migrated` : ''}`,
        variant: 'default',
      });
    } catch (error: any) {
      console.error('Migration error:', error);
      toast({
        title: 'Migration Failed',
        description: error.message || 'Error migrating service images',
        variant: 'destructive',
      });
      setResult({ error: String(error.message || error) });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Service Image Migration Tool</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-6">
          This tool will migrate existing service images to the new relational model. 
          Use this if you have services with images that were uploaded before the relational model was implemented.
        </p>
        
        <div className="flex items-center space-x-2 mb-6">
          <Switch
            id="force-mode"
            checked={forceMode}
            onCheckedChange={setForceMode}
          />
          <Label htmlFor="force-mode">
            Force mode (attempt to remigrate already migrated images)
          </Label>
        </div>
        
        {result && !result.error && (
          <Alert className="mt-4">
            <AlertTitle>Migration Results</AlertTitle>
            <AlertDescription>
              <div className="mt-2">
                <p>Total services processed: {result.total}</p>
                <p>Successfully migrated: {result.migrated}</p>
                {result.skipped !== undefined && <p>Skipped (already migrated): {result.skipped}</p>}
                <p>Failed: {result.failed}</p>
                {result.message && <p>Message: {result.message}</p>}
              </div>
            </AlertDescription>
          </Alert>
        )}
        
        {result?.error && (
          <Alert className="mt-4" variant="destructive">
            <AlertTitle>Migration Failed</AlertTitle>
            <AlertDescription>
              <div className="mt-2">
                <p>{result.error}</p>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button 
          onClick={handleMigrate} 
          disabled={loading}
        >
          {loading ? 'Migrating...' : 'Migrate Service Images'}
        </Button>
      </CardFooter>
    </Card>
  );
};
